import { api, type Member, type MemberList, type OrgOverview } from '../api';
import { callout, field, input, opt, openDialog, select, val } from '../dialog';
import { esc, fmtDate, html, pill, toast, type State } from '../ui';

/**
 * Who has a way in.
 *
 * The organisation could always record its staff — their licences, their
 * authorisations, who signed what — and could not give any of them a way to
 * log in. Signing up made one administrator; everybody after that was inserted
 * into the database by hand, or worked under a colleague's password.
 *
 * The second is what this screen exists to end. A signature snapshots the
 * signer's authorisation as it stood that day, ML.A.801 asks who released the
 * aircraft and ML.A.901 who reviewed it — questions a shared login cannot
 * answer. So each person gets their own, tied to the Person record the
 * certificates already point at.
 */

const ROLE_LABEL: Record<string, string> = {
  ACCOUNTABLE_MANAGER: 'Accountable manager',
  CAM_STAFF: 'CAM staff',
  ARS: 'Airworthiness review staff',
  CERTIFYING_STAFF: 'Certifying staff',
  PILOT: 'Pilot',
  COMPLIANCE: 'Compliance monitoring',
  ADMIN: 'Administrator',
  OWNER_PORTAL: 'Owner portal',
};

const ROLE_NOTE: Record<string, string> = {
  ACCOUNTABLE_MANAGER: 'answerable for the organisation; may administer it',
  CAM_STAFF: 'the programme, directives, planning and work packs',
  ARS: 'the airworthiness review — issuing the ARC needs the authorisation as well',
  CERTIFYING_STAFF: 'releases after maintenance, incoming inspection of parts',
  PILOT: 'their own tech-log entries and defects',
  COMPLIANCE: 'reads everything, signs nothing',
  ADMIN: 'customers, invoices, documents, and this screen',
  OWNER_PORTAL: 'one customer\'s own aircraft, and nothing else',
};

const STATE: Record<string, { tone: State; label: string }> = {
  ACTIVE: { tone: 'OK', label: 'Active' },
  INVITED: { tone: 'DUE_SOON', label: 'Invited' },
  REVOKED: { tone: 'NA', label: 'Revoked' },
};

export function membersCard(list: MemberList): string {
  const live = list.members.filter((m) => m.state !== 'REVOKED');
  const administrators = live.filter((m) =>
    m.roles.some((r) => r === 'ADMIN' || r === 'ACCOUNTABLE_MANAGER'),
  ).length;

  return html`
    <details class="card" data-members>
      <summary class="card-h">
        <div class="sum">
          <span class="twist" aria-hidden="true"></span>
          <div>
            <h2>Who can log in</h2>
            <p class="tiny dim">
              ${live.length} login(s)${list.unlinked.length ? ` · ${list.unlinked.length} of your staff have none` : ''}
            </p>
          </div>
        </div>
        <button class="btn sm pri" data-invite>Add somebody</button>
      </summary>

      <div class="tblwrap">
        <table class="tbl">
          <thead>
            <tr>
              <th class="wide">Person</th><th>Roles</th><th>Signs as</th>
              <th>Last seen</th><th></th><th></th>
            </tr>
          </thead>
          <tbody>
            ${list.members.length
              ? list.members.map((m) => memberRow(m)).join('')
              : '<tr><td colspan="6" class="muted">Only you, so far.</td></tr>'}
          </tbody>
        </table>
      </div>

      ${list.unlinked.length
        ? callout('info', 'i', html`
            <b>${list.unlinked.length} of your staff have no login.</b>
            ${esc(list.unlinked.slice(0, 4).map((p) => p.displayName).join(', '))}${
              list.unlinked.length > 4 ? ` and ${list.unlinked.length - 4} more` : ''
            }. Inviting them from here attaches the login to the record they already sign as,
            so what they sign tomorrow and what they signed last year name the same person.
          `)
        : ''}

      ${administrators < 2
        ? callout('caution', '!', html`
            <b>One administrator.</b> If that account is lost, nobody can invite, revoke or edit the
            organisation without a database. Give a second person the administrator role.
          `)
        : ''}

      ${explainer()}
    </details>
  `;
}

/**
 * The three objects, and why they are three.
 *
 * People ask this — reasonably, because most software has one "user" and this
 * has a staff record, a login and a membership. The answer is regulatory
 * rather than architectural, so it belongs on the screen where the confusion
 * happens rather than in a manual nobody opens.
 */
function explainer(): string {
  return html`
    <details class="card-f" style="cursor:pointer">
      <summary><b>Staff records, logins and roles — what is what</b></summary>
      <div style="margin-top:10px;display:flex;flex-direction:column;gap:12px;cursor:default">
        <div class="rows">
          <div class="row">
            <div class="bd">
              <div class="t">A staff record is the person</div>
              <div class="m">
                Their licence, their qualifications, their authorisations — and what a signature
                names. It exists whether or not they can log in, and it stays after they leave,
                because the certificates they signed name them for as long as those records are
                kept. Added and corrected on <b>Staff &amp; authorisations</b>.
              </div>
            </div>
          </div>
          <div class="row">
            <div class="bd">
              <div class="t">A login is a way in</div>
              <div class="m">
                One per person, never shared. Attaching it to their staff record is what makes
                what they sign tomorrow and what they signed last year the same person.
              </div>
            </div>
          </div>
          <div class="row">
            <div class="bd">
              <div class="t">Roles say what somebody works on</div>
              <div class="m">
                Which screens they use. They do <b>not</b> say what they may sign: a release, an
                airworthiness review or a programme approval needs an <b>authorisation</b>, granted
                against the qualifications ML.A.904 asks for. Nobody issues an ARC because their
                role says airworthiness review staff — they issue it because they hold the
                authorisation.
              </div>
            </div>
          </div>
        </div>

        ${callout('info', 'i', html`
          <b>In a small organisation one person is several of these at once.</b> The accountable
          manager of a club with two or three staff is usually also its CAM person and one of its
          pilots, and Part-ML is written expecting exactly that — so a login holds as many roles as
          the person really has. What is never acceptable is the other direction: two people behind
          one login. A signature snapshots the signer's authorisation as it stood that day, and
          ML.A.801 asks who released the aircraft while ML.A.901 asks who reviewed it. A shared
          account cannot answer either, and the record stops being evidence at the moment somebody
          asks.
        `)}
      </div>
    </details>
  `;
}

function memberRow(m: Member): string {
  const s = STATE[m.state] ?? { tone: 'NA' as State, label: m.state };
  const revoked = m.state === 'REVOKED';

  return html`
    <tr${revoked ? ' style="opacity:.55"' : ''}>
      <td class="wide">
        ${esc(m.displayName || m.email)}
        <span class="sub2">${esc(m.email)}</span>
      </td>
      <td>
        ${m.roles.map((r) => `<span class="fl fl-ref">${esc(ROLE_LABEL[r] ?? r)}</span>`).join(' ')}
      </td>
      <td class="tiny">
        ${m.person
          ? esc(m.person.displayName)
          : '<span style="color:var(--caution)">no staff record</span>'}
        ${m.person?.jobTitle ? `<span class="sub2">${esc(m.person.jobTitle)}</span>` : ''}
      </td>
      <td class="tiny dim">
        ${m.lastLoginAt ? esc(fmtDate(m.lastLoginAt)) : m.needsPassword ? 'not taken up yet' : 'never'}
      </td>
      <td>${pill(s.tone, s.label)}</td>
      <td style="white-space:nowrap">
        ${revoked
          ? ''
          : html`
              <button class="btn sm" data-roles="${esc(m.id)}">Roles</button>
              ${m.state === 'INVITED'
                ? html`<button class="btn sm" data-rename="${esc(m.id)}">Name</button>
                       <button class="btn sm" data-resend="${esc(m.id)}">Send again</button>`
                : ''}
              <button class="btn sm" data-revoke="${esc(m.id)}">Revoke</button>
            `}
      </td>
    </tr>
  `;
}

// ── the dialogs ─────────────────────────────────────────────────────────────

const roleBoxes = (roles: readonly string[], chosen: string[]) => html`
  <div class="rows">
    ${roles.map((r) => html`
      <label class="row" style="cursor:pointer;align-items:flex-start;gap:9px">
        <input type="checkbox" data-role="${esc(r)}" ${chosen.includes(r) ? 'checked' : ''}
               style="margin-top:3px">
        <div class="bd">
          <div class="t">${esc(ROLE_LABEL[r] ?? r)}</div>
          <div class="m tiny dim">${esc(ROLE_NOTE[r] ?? '')}</div>
        </div>
      </label>
    `).join('')}
  </div>
`;

const picked = (root: HTMLElement) =>
  [...root.querySelectorAll<HTMLInputElement>('[data-role]')].filter((b) => b.checked).map((b) => b.dataset.role!);

/**
 * Adding somebody. One dialog, whatever they turn out to need.
 *
 * There were two buttons — "Add person" and "Give somebody a login" — and
 * nothing on either said which one you wanted. They are not alternatives: a
 * staff record is the person, a login is a way into the software, and most
 * people in a small organisation need the first and never the second. But
 * that is an explanation, and a screen that needs one has already lost.
 *
 * So: one way in, asking the two questions in the order they actually arise —
 * who is this, and do they need to log in.
 */
export function addSomebody(o: OrgOverview, list: MemberList | null, reload: () => void): void {
  let existingPersonId = '';
  let wantsLogin = false;
  const roles = ['CAM_STAFF'];

  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Add somebody',
    sub: 'A staff record, a login, or both',
    primary: 'Add them',
    body: () => {
      const known = list?.unlinked ?? [];
      const isExisting = Boolean(existingPersonId);

      return html`
        ${known.length
          ? field('Who', select('personId', [
              opt('', 'Somebody new', !existingPersonId),
              ...known.map((p) => opt(p.id, `${p.displayName}${p.jobTitle ? ` — ${p.jobTitle}` : ''}`, p.id === existingPersonId)),
            ].join(''), 'data-person'),
            'Already on the staff list? Pick them, and this only gives them a way in')
          : ''}

        ${isExisting
          ? ''
          : html`
              <div class="fset">
                <div class="lg">The person</div>
                <div class="frow3">
                  ${field('First name', input('firstName', '', 'maxlength="80"'))}
                  ${field('Last name', input('lastName', '', 'maxlength="80"'))}
                  ${field('Job title', input('jobTitle', '', 'placeholder="Certifying staff" maxlength="120"'))}
                </div>
                <div class="frow">
                  ${field('Email', input('email', '', 'type="email" maxlength="254"'))}
                  ${field('Phone', input('phone', '', 'maxlength="40"'))}
                </div>
                <div class="frow3">
                  ${field('Licence', select('licenceKind', [
                    opt('NONE', 'None'),
                    opt('PART_66', 'Part-66'),
                    opt('FAA_AP', 'FAA A&P'),
                    opt('FAA_IA', 'FAA A&P with IA'),
                  ].join('')))}
                  ${field('Number', input('licenceNumber', '', 'placeholder="RO.66.2210" style="font-family:var(--mono)" maxlength="60"'))}
                  ${field('Category', input('licenceCategory', '', 'placeholder="B1.1" maxlength="24"'))}
                </div>
                ${field('Licence expiry', input('licenceExpiry', '', 'type="date"'))}
                <p class="tiny dim" style="margin:0">
                  This is the record a signature names. A release to service rests on a licence, so one
                  has to be on file before a CRS authorisation can be granted — that comes afterwards,
                  on the staff list.
                </p>
              </div>
            `}

        ${list
          ? html`
              <div class="fset">
                <div class="lg">Do they need to log in?</div>
                <div class="rows">
                  <label class="row" style="cursor:pointer;align-items:flex-start;gap:9px">
                    <input type="radio" name="wantsLogin" value="no" ${wantsLogin || isExisting ? '' : 'checked'}
                           data-login-choice style="margin-top:3px" ${isExisting ? 'disabled' : ''}>
                    <div class="bd">
                      <div class="t">No — a staff record only</div>
                      <div class="m tiny dim">
                        Most people. The contract engineer who signs a release twice a year, a
                        pilot-owner under ML.A.803, anyone whose licence you track. They can be named
                        on certificates and hold authorisations without ever opening FlyPack CAMO.
                      </div>
                    </div>
                  </label>
                  <label class="row" style="cursor:pointer;align-items:flex-start;gap:9px">
                    <input type="radio" name="wantsLogin" value="yes" ${wantsLogin || isExisting ? 'checked' : ''}
                           data-login-choice style="margin-top:3px">
                    <div class="bd">
                      <div class="t">Yes — invite them</div>
                      <div class="m tiny dim">
                        Their own login, never shared, and an email inviting them to set a password.
                        What they sign is then recorded against them by name, which is what makes it
                        defensible afterwards.
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            `
          : callout('info', 'i', html`
              Giving somebody a login needs an administrator. This adds the staff record; an
              administrator can give them a way in afterwards.
            `)}

        ${wantsLogin || isExisting
          ? html`
              ${isExisting
                ? field('Email', input('email', known.find((p) => p.id === existingPersonId)?.email ?? '',
                    'type="email" maxlength="254"'), 'Where the invitation goes')
                : ''}
              <div class="fset">
                <div class="lg">What they work on</div>
                ${roleBoxes(list?.roles ?? [], roles)}
              </div>
              ${field('Owner portal — whose aircraft', select('customerId', [
                opt('', 'Not an owner-portal login'),
                ...o.customers.map((c) => opt(c.id, c.name)),
              ].join('')), 'Only for an owner or customer: they see that customer\'s aircraft and nothing else')}
              ${callout('info', 'i', html`
                A role says which screens somebody works on. What they may <b>sign</b> — a release, an
                airworthiness review, a programme approval — is an authorisation on their staff record,
                granted separately against the qualifications ML.A.904 asks for.
              `)}
            `
          : ''}
      `;
    },
    onRender: (root, refresh) => {
      root.querySelector<HTMLSelectElement>('[data-person]')?.addEventListener('change', (e) => {
        existingPersonId = (e.target as HTMLSelectElement).value;
        // Somebody already on the list is only ever here for a login; there is
        // nothing else left to give them.
        if (existingPersonId) wantsLogin = true;
        refresh();
      });
      root.querySelectorAll<HTMLInputElement>('[data-login-choice]').forEach((b) =>
        b.addEventListener('change', () => {
          if (!b.checked) return;
          wantsLogin = b.value === 'yes';
          // Answering this question must not empty the form above it.
          const keep = new Map<string, string>();
          root.querySelectorAll<HTMLInputElement>('input[name], select[name]')
            .forEach((i) => { if (i.type !== 'radio' && i.type !== 'checkbox') keep.set(i.name, i.value); });
          refresh();
          root.querySelectorAll<HTMLInputElement>('input[name], select[name]').forEach((i) => {
            const was = keep.get(i.name);
            if (was !== undefined && i.type !== 'radio' && i.type !== 'checkbox') i.value = was;
          });
        }),
      );
    },
    collect: (root) => {
      const email = val(root, 'email');

      if (existingPersonId) {
        if (!email) return { error: 'Where should the invitation go?' };
        const chosen = picked(root);
        if (!chosen.length) return { error: 'Pick at least one thing they work on.' };
        return {
          mode: 'invite-existing', personId: existingPersonId, email, roles: chosen,
          customerId: val(root, 'customerId') || undefined,
        };
      }

      const firstName = val(root, 'firstName');
      const lastName = val(root, 'lastName');
      if (!firstName || !lastName) return { error: 'Both names are required.' };
      if (wantsLogin && !email) return { error: 'A login needs an email address to invite.' };

      const kind = val(root, 'licenceKind');
      const person = {
        firstName, lastName,
        jobTitle: val(root, 'jobTitle') || undefined,
        email: email || undefined,
        phone: val(root, 'phone') || undefined,
        licenceKind: kind === 'NONE' ? undefined : kind,
        licenceNumber: val(root, 'licenceNumber') || undefined,
        licenceCategory: val(root, 'licenceCategory') || undefined,
        licenceExpiry: val(root, 'licenceExpiry') || undefined,
      };
      if (!wantsLogin) return { mode: 'person-only', person };

      const chosen = picked(root);
      if (!chosen.length) return { error: 'Pick at least one thing they work on.' };
      if (chosen.includes('OWNER_PORTAL') && !val(root, 'customerId')) {
        return { error: 'An owner-portal login has to be attached to a customer.' };
      }
      return {
        mode: 'person-and-login', person, roles: chosen,
        customerId: val(root, 'customerId') || undefined,
      };
    },
    /**
     * The staff record first, then the login against it.
     *
     * Two calls, in that order on purpose: if the mail server is down the
     * person still exists — which is the half that was always going to matter
     * — and the invitation can be sent again from the list of logins.
     */
    submit: async (payload) => {
      const p = payload as Record<string, any>;
      if (p.mode === 'invite-existing') {
        return api.organisation.invite({
          email: p.email, roles: p.roles, personId: p.personId, customerId: p.customerId,
        });
      }
      const made = await api.organisation.createPerson(p.person);
      if (p.mode === 'person-only') return { person: made };
      const invited = await api.organisation.invite({
        email: p.person.email, firstName: p.person.firstName, lastName: p.person.lastName,
        roles: p.roles, personId: made.id, customerId: p.customerId,
      });
      return { person: made, ...invited };
    },
    done: (r: any) =>
      r.person && !r.membershipId
        ? `${r.person.displayName} added to the staff list — grant their authorisations next`
        : r.emailed
          ? 'Added, and invited — they set their own password from the link'
          : 'Added, but the invitation could not be emailed. Use "Send again" once mail is working.',
    onDone: reload,
  });
}

/**
 * Inviting somebody who is already on the staff list.
 *
 * Kept for the login list's own button, where the question is never "who is
 * this" — everybody on that screen already exists.
 */
export function inviteMember(list: MemberList, o: OrgOverview, reload: () => void): void {
  let roles = ['CAM_STAFF'];

  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Give somebody a login',
    sub: 'Theirs alone — what they sign is recorded against them by name',
    primary: 'Send the invitation',
    body: () => html`
      ${list.unlinked.length
        ? field('Somebody on the staff list', select('personId', [
            opt('', 'Not on the list — invite a new address'),
            ...list.unlinked.map((p) => opt(p.id, `${p.displayName}${p.jobTitle ? ` — ${p.jobTitle}` : ''}`)),
          ].join(''), 'data-person'), 'Ties the login to the record their certificates already name')
        : ''}

      <div class="frow3">
        ${field('Email', input('email', '', 'type="email" maxlength="254"'), 'Where the invitation goes')}
        ${field('First name', input('firstName', '', 'maxlength="80"'))}
        ${field('Last name', input('lastName', '', 'maxlength="80"'))}
      </div>

      <div class="fset" style="margin-top:4px" data-staff>
        <div class="lg">Their staff record</div>
        <label class="row" style="cursor:pointer;align-items:flex-start;gap:9px">
          <input type="checkbox" data-with-staff checked style="margin-top:3px">
          <div class="bd">
            <div class="t">Add them to the staff list as well</div>
            <div class="m tiny dim">
              A signature names a staff record, and an authorisation hangs off one. Without it this
              login works and can sign nothing.
            </div>
          </div>
        </label>
        ${field('Job title', input('jobTitle', '', 'maxlength="120" placeholder="Certifying staff"'),
          'Their licence and authorisations are added afterwards, on the staff list')}
      </div>

      <div class="fset" style="margin-top:4px">
        <div class="lg">What they work on</div>
        ${roleBoxes(list.roles, roles)}
      </div>

      ${field('Owner portal — whose aircraft', select('customerId', [
        opt('', 'Not an owner-portal login'),
        ...o.customers.map((c) => opt(c.id, c.name)),
      ].join('')), 'Only for an owner or customer: they see that customer\'s aircraft and nothing else')}

      ${callout('info', 'i', html`
        A role says which screens somebody works on. What they may <b>sign</b> — a release, an
        airworthiness review, a programme approval — is an authorisation on their staff record, and
        is granted separately against the qualifications ML.A.904 asks for.
      `)}
    `,
    onRender: (root) => {
      // Somebody already on the staff list has a record; offering to make them
      // a second one is how a personnel file ends up with two of the same
      // person.
      const staffBox = root.querySelector<HTMLElement>('[data-staff]');
      const showStaffOffer = (personChosen: boolean) => {
        if (staffBox) staffBox.style.display = personChosen ? 'none' : '';
      };

      // Choosing somebody from the staff list fills in what is already known
      // about them, so the address is the only thing left to type.
      root.querySelector<HTMLSelectElement>('[data-person]')?.addEventListener('change', (e) => {
        const id = (e.target as HTMLSelectElement).value;
        showStaffOffer(Boolean(id));
        const person = list.unlinked.find((p) => p.id === id);
        if (!person) return;
        const [first, ...rest] = person.displayName.split(' ');
        const set = (name: string, value: string) => {
          const box = root.querySelector<HTMLInputElement>(`[name="${name}"]`);
          if (box && !box.value) box.value = value;
        };
        set('email', person.email ?? '');
        set('firstName', first ?? '');
        set('lastName', rest.join(' '));
      });
    },
    collect: (root) => {
      roles = picked(root);
      const email = val(root, 'email');
      if (!email) return { error: 'Where should the invitation go?' };
      if (!roles.length) return { error: 'Pick at least one thing they work on.' };
      if (roles.includes('OWNER_PORTAL') && !val(root, 'customerId')) {
        return { error: 'An owner-portal login has to be attached to a customer.' };
      }
      const personId = val(root, 'personId') || undefined;
      const withStaffRecord = !personId
        && Boolean(root.querySelector<HTMLInputElement>('[data-with-staff]')?.checked);
      if (withStaffRecord && (!val(root, 'firstName') || !val(root, 'lastName'))) {
        return { error: 'A staff record needs a first and last name.' };
      }

      return {
        email,
        firstName: val(root, 'firstName') || undefined,
        lastName: val(root, 'lastName') || undefined,
        roles,
        personId,
        withStaffRecord,
        jobTitle: withStaffRecord ? val(root, 'jobTitle') || undefined : undefined,
        customerId: val(root, 'customerId') || undefined,
      };
    },
    submit: (body) => api.organisation.invite(body),
    done: (r) => {
      const staff = r.staffRecordCreated ? ' · added to the staff list' : '';
      if (!r.emailed) {
        return `Login created${staff}, but the invitation could not be emailed. Use "Send again" once mail is working.`;
      }
      return r.needsPassword
        ? `Invitation sent${staff} — they set their own password from the link`
        : `Added${staff} — they already have a FlyPack CAMO account and can sign in now`;
    },
    onDone: reload,
  });
}

export function editRoles(member: Member, list: MemberList, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    title: 'What they work on',
    sub: member.displayName || member.email,
    primary: 'Save the roles',
    body: () => html`
      ${roleBoxes(list.roles, member.roles)}
      ${callout('info', 'i', html`
        This does not change what they may sign. A release, an airworthiness review or a programme
        approval needs an <b>authorisation</b> on their staff record.
      `)}
    `,
    collect: (root) => {
      const roles = picked(root);
      if (!roles.length) return { error: 'Somebody has to be something.' };
      return { roles };
    },
    submit: (body) => api.organisation.setRoles(member.id, body.roles as string[]),
    done: () => 'Roles saved',
    onDone: reload,
  });
}

/**
 * Taking a login away.
 *
 * Never a delete, and the dialog says so: the releases and reviews they signed
 * are records with a retention period measured in years, and the person who
 * signed them stays resolvable long after they have gone.
 */
export function revokeMember(member: Member, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    title: 'Revoke this login',
    sub: member.displayName || member.email,
    primary: 'Revoke it',
    body: () => html`
      ${field('Why', input('reason', '', 'maxlength="400" placeholder="Left the organisation"'))}
      ${callout('caution', '!', html`
        They are signed out everywhere immediately. Everything they signed stays exactly as it is —
        a certificate names the person who issued it for as long as the record is kept, and their
        staff record stays with it.
      `)}
    `,
    collect: (root) => ({ reason: val(root, 'reason') || undefined }),
    submit: (body) => api.organisation.revokeMember(member.id, body.reason as string | undefined),
    done: () => 'Revoked',
    onDone: reload,
  });
}

/**
 * A typo in an invitation, before anybody has signed in to it.
 *
 * Only then. Once the account is in use the name belongs to the person using
 * it — it is what the audit trail says signed in, and an administrator
 * renaming a working colleague is an administrator editing somebody else's
 * identity. They change it themselves from their own account.
 */
export function renameInvitee(member: Member, reload: () => void): void {
  const [first, ...rest] = (member.displayName || '').split(' ');

  openDialog<Record<string, unknown>>({
    title: 'Correct the name',
    sub: `${member.email} — invitation not taken up yet`,
    primary: 'Save the name',
    body: () => html`
      <div class="frow">
        ${field('First name', input('firstName', first ?? '', 'maxlength="80"'))}
        ${field('Last name', input('lastName', rest.join(' '), 'maxlength="80"'))}
      </div>
      ${callout('info', 'i', html`
        This is the name on the login. Once they have signed in it is theirs to change, not yours —
        and the name on certificates is their staff record, corrected on the list above.
      `)}
    `,
    collect: (root) => {
      const firstName = val(root, 'firstName');
      const lastName = val(root, 'lastName');
      if (!firstName || !lastName) return { error: 'A name has two halves here.' };
      return { firstName, lastName };
    },
    submit: (body) => api.organisation.renameInvitee(member.id, body),
    done: () => 'Name corrected',
    onDone: reload,
  });
}

/** Wires the card into whatever screen is showing it. */
export function bindMembers(host: HTMLElement, list: MemberList, o: OrgOverview, reload: () => void): void {
  const find = (id: string) => list.members.find((m) => m.id === id);

  host.querySelector('[data-invite]')?.addEventListener('click', (e) => {
    // The button lives in a <summary>; without this the click folds the card.
    e.preventDefault();
    addSomebody(o, list, reload);
  });
  host.querySelectorAll<HTMLElement>('[data-roles]').forEach((b) =>
    b.addEventListener('click', () => {
      const m = find(b.dataset.roles!);
      if (m) editRoles(m, list, reload);
    }),
  );
  host.querySelectorAll<HTMLElement>('[data-revoke]').forEach((b) =>
    b.addEventListener('click', () => {
      const m = find(b.dataset.revoke!);
      if (m) revokeMember(m, reload);
    }),
  );
  host.querySelectorAll<HTMLElement>('[data-rename]').forEach((b) =>
    b.addEventListener('click', () => {
      const m = find(b.dataset.rename!);
      if (m) renameInvitee(m, reload);
    }),
  );
  host.querySelectorAll<HTMLElement>('[data-resend]').forEach((b) =>
    b.addEventListener('click', async () => {
      try {
        const r = await api.organisation.resendInvite(b.dataset.resend!);
        toast(r.emailed ? 'Invitation sent again.' : 'Could not send it — check the mail settings.');
      } catch (err) {
        toast(err instanceof Error ? err.message : 'That could not be sent.', 'error');
      }
    }),
  );
}
